import { useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { MoreHorizontal, Download, ExternalLink } from "lucide-react";
import { useInvoices } from "@/api/invoices";
import { CreateInvoiceButton } from "./create-invoice-button";

export function InvoicesTable() {
  const [page, setPage] = useState(1);
  const [limit] = useState(10);

  const { data: invoicesResponse } = useInvoices(page, limit);

  return (
    <div className="space-y-4">
      <div className="flex justify-end mb-4 gap-4">
        <CreateInvoiceButton />
      </div>
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Invoice Details</TableHead>
              <TableHead>Customer</TableHead>
              <TableHead>Amount</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Orders</TableHead>
              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {invoicesResponse?.data.map((invoice) => (
              <TableRow key={invoice.id}>
                <TableCell>
                  <div>
                    <p className="font-medium">
                      #{invoice.stripeInvoiceData.number}
                    </p>
                    <p className="text-sm text-gray-500">
                      {new Date(invoice.createdAt).toLocaleDateString()}
                    </p>
                    {invoice.stripeInvoiceData.dueDate && (
                      <p className="text-xs text-gray-400">
                        Due:{" "}
                        {new Date(
                          invoice.stripeInvoiceData.dueDate
                        ).toLocaleDateString()}
                      </p>
                    )}
                  </div>
                </TableCell>
                <TableCell>
                  <div>
                    <p className="font-medium">
                      {invoice.customer.user.firstName}{" "}
                      {invoice.customer.user.lastName}
                    </p>
                    <p className="text-sm text-gray-500">
                      {invoice.customer.user.email}
                    </p>
                  </div>
                </TableCell>
                <TableCell>
                  <div className="font-medium">
                    ${(invoice.stripeInvoiceData.amount / 100).toFixed(2)}
                  </div>
                </TableCell>
                <TableCell>
                  <div className="capitalize">
                    {invoice.stripeInvoiceData.status}
                  </div>
                </TableCell>
                <TableCell>
                  <div>
                    <p className="font-medium">{invoice.totalOrders} orders</p>
                    {invoice.orderDateRange && (
                      <p className="text-xs text-gray-400">
                        {new Date(
                          invoice.orderDateRange.start
                        ).toLocaleDateString()}{" "}
                        -{" "}
                        {new Date(
                          invoice.orderDateRange.end
                        ).toLocaleDateString()}
                      </p>
                    )}
                  </div>
                </TableCell>
                <TableCell>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" className="h-8 w-8 p-0">
                        <span className="sr-only">Open menu</span>
                        <MoreHorizontal className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      {invoice.stripeInvoiceData.hostedUrl && (
                        <DropdownMenuItem
                          onClick={() =>
                            window.open(
                              invoice.stripeInvoiceData.hostedUrl!,
                              "_blank"
                            )
                          }
                        >
                          <ExternalLink className="mr-2 h-4 w-4" />
                          View Invoice
                        </DropdownMenuItem>
                      )}
                      {invoice.stripeInvoiceData.pdfUrl && (
                        <DropdownMenuItem
                          onClick={() =>
                            window.open(
                              invoice.stripeInvoiceData.pdfUrl!,
                              "_blank"
                            )
                          }
                        >
                          <Download className="mr-2 h-4 w-4" />
                          Download PDF
                        </DropdownMenuItem>
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Pagination */}
      <div className="flex items-center justify-between px-2">
        <div className="text-sm text-gray-500">
          Showing {(page - 1) * limit + 1} to{" "}
          {Math.min(page * limit, invoicesResponse?.metadata.total ?? 0)} of{" "}
          {invoicesResponse?.metadata.total ?? 0} results
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPage(page - 1)}
            disabled={page <= 1}
          >
            Previous
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPage(page + 1)}
            disabled={page >= (invoicesResponse?.metadata.totalPages ?? 0)}
          >
            Next
          </Button>
        </div>
      </div>
    </div>
  );
}
